import React, { useContext, useEffect, useState } from 'react';
import { Card } from '../../../components/Card/Card';
import { Icon } from '../../../components/Icon/Icon';
import {
  Product,
  patchProduct,
  duplicateProduct,
  deleteProduct,
  getProductName,
  getProduct,
} from '../../../model/product';
import { handleError } from '../../../model/notification';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { StatusPill } from '../../../components/StatusPill/StatusPill';
import { t } from '../../../locales/translate';
import { ActivitiesContext } from '../../../components/Activities/Activities';
import { CombinedProducts } from './CombinedProducts';
import { compact } from 'lodash';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { Ownerships } from '../../Ownerships/Ownerships';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { AssignCompany, CompanyAssign } from '../../Ownerships/AssignCompany';
import { deleteOwnership, Ownership } from '../../../model/ownerships';

export function ProductHeader({ product }: { product: Product }): JSX.Element {
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [addOwnershipModal, setAddOwnershipModal] = useState(false);
  const [selectedOwnership, setSelectedOwnership] = useState<Ownership | null>(null);

  const { reloadActivities } = useContext(ActivitiesContext);

  async function handleDelete(): Promise<void> {
    if (!selectedOwnership) {
      return;
    }
    await deleteOwnership({ ownershipId: selectedOwnership.id });
    await getProduct(product.id);
    setSelectedOwnership(null);
  }

  const { confirm: confirmDeleteOwnership } = useConfirmation({
    title: t.model.company.removeCompany,
    message: t.model.company.confirmRemoveCompany,
    action: handleDelete,
    onClose: () => setSelectedOwnership(null),
  });

  useEffect(() => {
    if (selectedOwnership) {
      confirmDeleteOwnership();
    }
  }, [selectedOwnership]);

  async function updateProductStatus(status: 'on_sale' | 'stopped'): Promise<void> {
    setLoadingStatus(true);
    try {
      await patchProduct(product.id, { status });
      reloadActivities();
    } catch (error) {
      handleError(error);
    }
    setLoadingStatus(false);
  }

  const { confirm: confirmDelete } = useConfirmation({
    title: t.actions.delete(t.model.product._name),
    message: t.general.confirmDelete(t.model.product._name),
    action: async () => await deleteProduct(product.id),
  });

  const { confirm: confirmDuplicate } = useConfirmation({
    title: t.actions.duplicate(t.model.product._name),
    message: t.general.confirmDuplicate(t.model.product._name),
    action: async () => await duplicateProduct(product.id),
  });

  const hasCombinedProducts =
    product.combinedProductIds !== null &&
    !!compact(product.combinedProductIds?.map((item) => (item.length ? item : null)) || []).length;

  const addOwnershipButton = (
    <Tooltip
      position="bottom"
      disableRepositioning={false}
      reference={<Icon name="users" />}
      arrow
      elevated
      gap="medium"
      mode="click"
    >
      <Ownerships
        product={product}
        onAddCompany={() => setAddOwnershipModal(true)}
        onDeleteCompany={setSelectedOwnership}
      />
    </Tooltip>
  );

  return (
    <div>
      <Card
        className="product-header"
        elevation={1}
        actions={
          <div className="product-header__icons">
            {(product.status === 'draft' || product.status === 'stopped') && (
              <PermissionButton
                name="play"
                onClick={() => updateProductStatus('on_sale')}
                model={product}
                permission="change_on_sale"
                loading={loadingStatus}
                tooltip={t.model.product.startSale}
              />
            )}
            {product.status === 'on_sale' && (
              <PermissionButton
                name="pause"
                onClick={() => updateProductStatus('stopped')}
                model={product}
                permission="change_on_sale"
                tooltip={t.model.product.stopSale}
              />
            )}
            <PermissionButton model={product} permission="destroy" name="trash" onClick={confirmDelete} hideLock />
            <PermissionButton
              name="copy"
              onClick={confirmDuplicate}
              // Fixes bug that tooltip does not display when hovering from the left. TODO: Investigate tooltip behaviour
              style={{ zIndex: 1 }}
              disabled={hasCombinedProducts}
              tooltip={hasCombinedProducts ? t.model.product.cantClone : ''}
              model={product}
              permission="duplicate"
              hideLock
            />
            {addOwnershipButton}
          </div>
        }
      >
        <div className="product-header__details">
          <h1 className="user-details__name">{getProductName(product)}</h1>
          <div className="product-header__bottom">
            <span className="product-header__sku">{product.sku}</span>
            <StatusPill status={product.status} />
          </div>
        </div>
        {hasCombinedProducts && <CombinedProducts product={product} />}
      </Card>
      <ControlledModal<CompanyAssign>
        open={addOwnershipModal}
        onClose={() => setAddOwnershipModal(false)}
        title={t.actions.assign(t.model.company._name)}
        size="small"
      >
        {(controller) => (
          <AssignCompany
            productId={product.id}
            type="Product"
            controller={controller}
            onOwnershipCreated={async () => await getProduct(product.id)}
          />
        )}
      </ControlledModal>
    </div>
  );
}
